// src/pages/Coleta/detalhe/SecoesDetalhe.jsx
import React from 'react';
import {
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Grid,
    Paper,
    Skeleton,
    Stack,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';
import * as Lucide from 'lucide-react';
import { PiTestTubeDuotone } from 'react-icons/pi';
import dayjs from 'dayjs';
import { valorParaTexto } from '../../../utils/numeroLab';
import SerialDispositivo from '../components/SerialDispositivo';
import {
    dataDoExperimento,
    formatarContagem,
    formatarDataHora,
    rotuloDiluicao,
    rotuloTurno,
    tempoDecorrido,
    formatarLog10,
} from '../../../utils/coleta';

/** Cartão padrão das seções: borda leve, cantos de 12px, título opcional. */
export function Secao({ titulo, acao, destaque = false, children, sx }) {
    const theme = useTheme();
    return (
        <Paper
            variant="outlined"
            sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 3,
                breakInside: 'avoid',
                ...(destaque && { borderTop: `3px solid ${theme.palette.primary.main}` }),
                ...sx,
            }}
        >
            {(titulo || acao) && (
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    useFlexGap
                    flexWrap="wrap"
                    columnGap={1}
                    rowGap={0.5}
                    sx={{ mb: 2 }}
                >
                    {titulo && <Typography fontWeight={700} sx={{ fontSize: '1.1rem' }}>{titulo}</Typography>}
                    {acao}
                </Stack>
            )}
            {children}
        </Paper>
    );
}

const Rodape = ({ children }) => (
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
        {children}
    </Typography>
);

const NORMA_AGUA = 'Portaria GM/MS Nº 888/2021';

/** Presença/ausência de cada alvo, como no modal de processar das Amostras. */
function PresencaAlvo({ rotulo, presenca }) {
    const theme = useTheme();
    const conhecida = presenca === true || presenca === false;
    const cor = presenca === true ? theme.palette.error.main : presenca === false ? theme.palette.success.main : theme.palette.text.disabled;
    return (
        <Paper
            variant="outlined"
            sx={{
                display: 'flex',
                height: '100%',
                overflow: 'hidden',
                borderRadius: 3,
                borderColor: conhecida ? alpha(cor, 0.3) : 'divider',
                bgcolor: conhecida ? alpha(cor, 0.06) : 'background.paper',
            }}
        >
            <Box sx={{ width: 56, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRight: 1, borderColor: 'divider' }}>
                <PiTestTubeDuotone size={28} color={cor} />
            </Box>
            <Box sx={{ p: 2, minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
                    {rotulo}
                </Typography>
                <Typography variant="h5" fontWeight={800} sx={{ color: cor }}>
                    {presenca === true ? 'PRESENÇA' : presenca === false ? 'AUSÊNCIA' : '—'}
                    {conhecida && (
                        <Typography component="span" fontWeight={700} sx={{ ml: 1, opacity: 0.8 }}>
                            em 100 mL
                        </Typography>
                    )}
                </Typography>
                {conhecida && (
                    <Stack spacing={0.25} sx={{ mt: 1, pt: 1, borderTop: `1px solid ${alpha(theme.palette.divider, 0.6)}` }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <Lucide.ShieldCheck size={14} color={theme.palette.text.secondary} />
                            <Typography variant="caption" color="text.secondary">Padrão Portaria 888: ausência em 100 mL</Typography>
                        </Stack>
                        <Typography variant="caption" fontWeight={800} sx={{ color: cor }}>
                            STATUS: {presenca ? 'NÃO CONFORME' : 'CONFORME'}
                        </Typography>
                    </Stack>
                )}
            </Box>
        </Paper>
    );
}

/** Método e base normativa da água, nos mesmos cartões do modal de processar. */
function ReferenciaAgua() {
    const theme = useTheme();
    const cartao = (Icone, rotulo, valor, detalhe) => (
        <Grid item xs={12} sm={6}>
            <Paper
                variant="outlined"
                sx={{
                    p: 2,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    borderRadius: 2,
                    borderColor: alpha(theme.palette.primary.main, 0.15),
                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                }}
            >
                <Icone size={28} color={theme.palette.primary.main} style={{ flexShrink: 0 }} />
                <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">{rotulo}</Typography>
                    <Typography variant="body2" fontWeight={700}>{valor}</Typography>
                    {detalhe && <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>{detalhe}</Typography>}
                </Box>
            </Paper>
        </Grid>
    );
    return (
        <Grid container spacing={2} sx={{ mt: 0 }}>
            {cartao(Lucide.Microscope, 'Método de Referência', 'NMP/100 mL', 'Limite de detecção: < 1,8 NMP/100 mL')}
            {cartao(Lucide.Scale, 'Base Normativa', NORMA_AGUA)}
        </Grid>
    );
}

/** Resultado do modelo, ou o que falta para ele existir. */
export function ResultadoCard({
    resumo,
    processamento,
    unidade,
    rotuloMedida,
    dispositivo,
    agora,
    processando,
    podeProcessar,
    onProcessar,
    podeRestaurar,
    restaurando,
    onRestaurar,
    mostrarDiluicao,
    alvosQualitativos,
}) {
    const theme = useTheme();
    const exp = resumo.experimento;
    const inicio = dataDoExperimento(exp?.inicio);
    const fim = dataDoExperimento(exp?.fim);
    const ativo = String(exp?.status).toLowerCase() === 'active';

    // Água (presença/ausência, Portaria 888): um resultado por alvo, do mesmo teste.
    const qualitativo = Boolean(alvosQualitativos?.length);
    const presencas =
        qualitativo && exp && !exp.excluido && !processando && exp.processado
            ? alvosQualitativos.map((a) => {
                  const r = (exp.alvos || []).find((x) => x.alvo === a.alvo);
                  return { ...a, presenca: r ? r.presenca : exp.ausente ? false : null };
              })
            : null;
    const detectados = presencas?.filter((p) => p.presenca === true) || [];
    const todosAusentes = Boolean(presencas?.length) && presencas.every((p) => p.presenca === false);
    const titulo = qualitativo ? alvosQualitativos.map((a) => a.rotulo).join(' e ') : rotuloMedida;

    let principal;
    let explicacao = null;
    if (presencas && (detectados.length || todosAusentes)) {
        principal = (
            <Stack direction="row" spacing={1.25} alignItems="center">
                {todosAusentes ? <Lucide.CircleCheck size={30} color={theme.palette.success.main} /> : <Lucide.TriangleAlert size={30} color={theme.palette.error.main} />}
                <Typography fontWeight={800} color={todosAusentes ? 'success.main' : 'error.main'} sx={{ fontSize: { xs: '1.75rem', sm: '2.25rem' } }}>
                    {todosAusentes ? 'Qualidade segura' : 'Risco microbiológico'}
                </Typography>
            </Stack>
        );
        explicacao = todosAusentes
            ? `Ausência de ${titulo} em 100 mL, como exige a ${NORMA_AGUA}.`
            : `Presença de ${detectados.map((d) => d.rotulo).join(' e ')} em 100 mL. A ${NORMA_AGUA} exige ausência.`;
    } else if (exp?.excluido) {
        principal = <Typography fontWeight={800} color="error.main" sx={{ fontSize: { xs: '1.75rem', sm: '2.25rem' } }}>Ensaio excluído</Typography>;
        explicacao = 'O experimento desta amostra foi excluído na página de Amostras. O resultado volta quando ele for restaurado.';
    } else if (!exp) {
        principal = <Typography fontWeight={800} sx={{ fontSize: { xs: '1.75rem', sm: '2.25rem' } }}>Aguardando tubo</Typography>;
        explicacao = `O ensaio começa quando o tubo entrar em ${dispositivo || 'no dispositivo'}.`;
    } else if (processando) {
        principal = (
            <Stack direction="row" spacing={1.5} alignItems="center">
                <CircularProgress size={28} />
                <Typography fontWeight={800} sx={{ fontSize: { xs: '1.75rem', sm: '2.25rem' } }}>Processando…</Typography>
            </Stack>
        );
    } else if (exp.ausente) {
        principal = <Typography fontWeight={800} color="success.main" sx={{ fontSize: { xs: '1.75rem', sm: '2.25rem' } }}>Ausente</Typography>;
        explicacao = 'O modelo não detectou a presença do alvo nesta amostra.';
    } else if (Number.isFinite(exp.log10Contagem)) {
        principal = (
            <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
                <Typography fontWeight={800} sx={{ fontSize: { xs: '2.25rem', sm: '2.75rem' }, lineHeight: 1.1 }}>
                    {formatarContagem(exp.log10Contagem)}
                </Typography>
                <Typography color="text.secondary">{unidade}</Typography>
                <Typography color="text.secondary" variant="body2">· {formatarLog10(exp.log10Contagem)}</Typography>
            </Stack>
        );
    } else if (ativo) {
        principal = <Typography fontWeight={800} sx={{ fontSize: { xs: '1.75rem', sm: '2.25rem' } }}>Em análise</Typography>;
        explicacao = inicio ? `${tempoDecorrido(inicio, agora)} decorridos.` : null;
    } else {
        principal = <Typography fontWeight={800} sx={{ fontSize: { xs: '1.75rem', sm: '2.25rem' } }}>Não processada</Typography>;
        explicacao = 'O ensaio terminou. Processe para obter o resultado do modelo.';
    }

    return (
        <Secao destaque>
            <Typography fontWeight={700}>{titulo}</Typography>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 2 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />
                <Typography variant="caption" color="text.secondary">Resultado do modelo BioAILab</Typography>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
                <Box>
                    {principal}
                    {explicacao && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{explicacao}</Typography>}
                </Box>
                {exp?.excluido && podeRestaurar && (
                    <Button
                        variant="outlined"
                        startIcon={<Lucide.RotateCcw size={18} />}
                        onClick={onRestaurar}
                        disabled={restaurando}
                        sx={{ borderRadius: 2, flexShrink: 0 }}
                        data-nao-imprimir
                    >
                        {restaurando ? 'Restaurando...' : 'Restaurar ensaio'}
                    </Button>
                )}
                {exp && !exp.excluido && !ativo && !processando && podeProcessar && (
                    <Button
                        variant={processamento ? 'outlined' : 'contained'}
                        disableElevation
                        startIcon={processamento ? <Lucide.RotateCcw size={18} /> : <Lucide.FlaskConical size={18} />}
                        onClick={onProcessar}
                        sx={{ borderRadius: 2, flexShrink: 0 }}
                        data-nao-imprimir
                    >
                        {processamento ? 'Reprocessar' : 'Processar'}
                    </Button>
                )}
            </Stack>

            {presencas && (
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    {presencas.map((p) => (
                        <Grid item xs={12} sm={6} key={p.alvo}>
                            <PresencaAlvo rotulo={p.rotulo} presenca={p.presenca} />
                        </Grid>
                    ))}
                </Grid>
            )}
            {qualitativo && <ReferenciaAgua />}

            <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px dashed ${alpha(theme.palette.divider, 0.9)}` }}>
                {processamento ? (
                    <>
                        <Rodape>
                            Modelo {processamento.modelo || 'BioAILab'}
                            {mostrarDiluicao ? ` · diluição ${rotuloDiluicao(exp?.diluicao)}` : ''}
                        </Rodape>
                        <Rodape>
                            Processada em {formatarDataHora(new Date(processamento.processadoEm))}
                            {processamento.processadoPor ? ` por ${processamento.processadoPor}` : ''}
                        </Rodape>
                    </>
                ) : (
                    <Rodape>Ainda sem resultado do modelo.</Rodape>
                )}
                {fim && <Rodape>Ensaio finalizado em {formatarDataHora(fim)}</Rodape>}
            </Box>
        </Secao>
    );
}

const TEXTO_COMPARACAO = {
    success: 'Modelo e laboratório concordam.',
    warning: 'Diferença moderada entre modelo e laboratório.',
    error: 'Diferença alta entre modelo e laboratório.',
};

/** Resultado de laboratório e, quando há os dois lados, a comparação com o modelo. */
export function LaboratorioCard({
    temExperimento,
    carregando,
    labResult,
    entradaPagina,
    comparacao,
    log10Modelo,
    unidade,
    podeLancar,
    onLancar,
}) {
    const acao =
        temExperimento && podeLancar ? (
            <Button size="small" variant="outlined" startIcon={<Lucide.Microscope size={16} />} onClick={onLancar} sx={{ borderRadius: 2 }} data-nao-imprimir>
                {labResult ? 'Atualizar' : 'Adicionar resultado'}
            </Button>
        ) : null;

    let conteudo;
    if (!temExperimento) {
        conteudo = <Typography variant="body2" color="text.secondary">Disponível depois que o tubo entrar no dispositivo.</Typography>;
    } else if (carregando) {
        conteudo = <Skeleton variant="rounded" height={56} />;
    } else if (!labResult) {
        conteudo = <Typography variant="body2" color="text.secondary">Nenhum resultado de laboratório lançado para esta amostra.</Typography>;
    } else {
        const deltaTexto = comparacao
            ? `${comparacao.delta > 0 ? '+' : ''}${comparacao.delta.toFixed(2).replace('.', ',')} log`
            : null;
        conteudo = (
            <Stack spacing={2}>
                <Grid container spacing={2}>
                    {(labResult.bacterias || []).map((b, i) => (
                        <Grid item xs={12} sm={6} key={`${b.bacteriaId || i}|${b.unit}`}>
                            <Typography variant="caption" color="text.secondary">{b.bacteriaNome}</Typography>
                            <Typography fontWeight={700}>
                                {Number.isFinite(b.nmp) ? valorParaTexto(b.nmp) : '—'}{' '}
                                <Typography component="span" variant="caption" color="text.secondary">{b.unit}</Typography>
                            </Typography>
                        </Grid>
                    ))}
                </Grid>
                {labResult.analysisDate && (
                    <Typography variant="caption" color="text.secondary">
                        Analisado no laboratório em {dayjs(labResult.analysisDate).format('DD/MM/YYYY')}
                    </Typography>
                )}

                {comparacao ? (
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 4 }} alignItems={{ sm: 'center' }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Modelo</Typography>
                                <Typography fontWeight={700}>{formatarContagem(log10Modelo)} {unidade}</Typography>
                                <Typography variant="caption" color="text.secondary">{formatarLog10(log10Modelo)}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Laboratório</Typography>
                                <Typography fontWeight={700}>{valorParaTexto(entradaPagina.nmp)} {entradaPagina.unit}</Typography>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Chip size="small" color={comparacao.cor} label={`Diferença ${deltaTexto}`} sx={{ fontWeight: 700 }} />
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                    {TEXTO_COMPARACAO[comparacao.cor]}
                                </Typography>
                            </Box>
                        </Stack>
                    </Paper>
                ) : (
                    <Typography variant="caption" color="text.secondary">
                        {!Number.isFinite(log10Modelo)
                            ? 'Sem resultado do modelo para comparar.'
                            : `Nenhum valor de laboratório em ${unidade} para comparar com o modelo.`}
                    </Typography>
                )}
            </Stack>
        );
    }

    return (
        <Secao titulo="Resultado de laboratório" acao={acao}>
            {conteudo}
        </Secao>
    );
}

const Campo = ({ rotulo, children }) => (
    <Grid item xs={12} sm={6}>
        <Typography variant="body2" color="text.secondary">{rotulo}</Typography>
        <Typography sx={{ mt: 0.5, fontSize: '1.02rem' }}>{children || '—'}</Typography>
    </Grid>
);

/** Dados do registro e do ensaio, em pares rótulo/valor. */
export function InformacoesCard({ resumo, dispositivoNome, agora, mostrarDiluicao }) {
    const exp = resumo.experimento;
    const coleta = resumo.coleta || {};
    const inicio = dataDoExperimento(exp?.inicio);
    const fim = dataDoExperimento(exp?.fim);
    const ativo = String(exp?.status).toLowerCase() === 'active';
    const tempo = inicio ? tempoDecorrido(inicio, fim || agora) : null;

    return (
        <Secao
            titulo="Informações da amostra"
            acao={
                coleta.registradoPor && (
                    <Stack direction="row" spacing={0.75} alignItems="center">
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'text.disabled' }} />
                        <Typography variant="caption" color="text.secondary">Registrado por {coleta.registradoPor}</Typography>
                    </Stack>
                )
            }
        >
            <Grid container spacing={2.5}>
                <Campo rotulo="Coleta">{coleta.dataHora && formatarDataHora(new Date(coleta.dataHora))}</Campo>
                <Campo rotulo="Início da análise">{inicio ? formatarDataHora(inicio) : 'Aguardando tubo'}</Campo>
                <Campo rotulo="Dispositivo">
                    {dispositivoNome && <Box component="span" sx={{ display: 'block' }}>{dispositivoNome}</Box>}
                    <SerialDispositivo serial={resumo.numeroSerie} />
                </Campo>
                <Campo rotulo="Operador">{coleta.registradoPor}</Campo>
                <Campo rotulo="Temperatura registrada">
                    {Number.isFinite(resumo.temperaturaAmostra)
                        ? `${String(resumo.temperaturaAmostra).replace('.', ',')} °C`
                        : 'Não informada'}
                </Campo>
                <Campo rotulo="Ordenha">{rotuloTurno(coleta.turno)}</Campo>
                <Campo rotulo="Tempo de análise">{tempo ? (ativo ? `${tempo} (em andamento)` : tempo) : null}</Campo>
                {mostrarDiluicao && <Campo rotulo="Diluição">{exp ? rotuloDiluicao(exp.diluicao) : null}</Campo>}
            </Grid>
        </Secao>
    );
}

/** Observações: comentários da amostra, mais recentes primeiro (autor e data em cada). */
export function ObservacoesCard({ comentarios, podeComentar, onAbrir }) {
    const ordenados = [...(comentarios || [])].sort((a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf());
    const visiveis = ordenados.slice(0, 3);
    return (
        <Secao
            titulo="Observações"
            acao={
                (podeComentar || ordenados.length > visiveis.length) && (
                    <Button size="small" startIcon={<Lucide.MessageSquarePlus size={16} />} onClick={onAbrir} sx={{ borderRadius: 2 }} data-nao-imprimir>
                        {podeComentar ? 'Adicionar' : `Ver todas (${ordenados.length})`}
                    </Button>
                )
            }
        >
            {visiveis.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Nenhuma observação registrada.</Typography>
            ) : (
                <Stack spacing={2}>
                    {visiveis.map((c, i) => (
                        <Stack key={`${c.createdAt}-${i}`} direction="row" spacing={1.5} alignItems="flex-start">
                            <Avatar sx={{ width: 30, height: 30, fontSize: '0.85rem' }}>
                                {String(c.author || '?').charAt(0).toUpperCase()}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="caption" color="text.secondary">
                                    {c.author} · {dayjs(c.createdAt).format('DD/MM/YYYY HH:mm')}
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{c.text}</Typography>
                            </Box>
                        </Stack>
                    ))}
                    {ordenados.length > visiveis.length && (
                        <Button size="small" onClick={onAbrir} sx={{ alignSelf: 'flex-start' }} data-nao-imprimir>
                            Ver todas ({ordenados.length})
                        </Button>
                    )}
                </Stack>
            )}
        </Secao>
    );
}

/** Etapas da amostra; as que ainda não aconteceram aparecem aguardando. */
export function LinhaDoTempo({ etapas }) {
    const theme = useTheme();
    return (
        <Secao titulo="Linha do tempo" sx={{ height: '100%' }}>
            <Stack>
                {etapas.map((etapa, i) => {
                    const feita = Boolean(etapa.data);
                    const ultima = i === etapas.length - 1;
                    return (
                        <Stack key={etapa.rotulo} direction="row" spacing={1.5}>
                            <Stack alignItems="center">
                                <Box
                                    sx={{
                                        width: 26,
                                        height: 26,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        border: `1px solid ${feita ? alpha(theme.palette.primary.main, 0.4) : theme.palette.divider}`,
                                        bgcolor: feita ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                                        color: feita ? 'primary.main' : 'text.disabled',
                                    }}
                                >
                                    {feita ? <Lucide.Check size={14} /> : <Lucide.Clock size={14} />}
                                </Box>
                                {!ultima && <Box sx={{ width: '1px', flex: 1, minHeight: 28, bgcolor: 'divider' }} />}
                            </Stack>
                            <Box sx={{ pb: ultima ? 0 : 2 }}>
                                <Typography variant="body2" fontWeight={700} color={feita ? 'text.primary' : 'text.secondary'}>
                                    {etapa.rotulo}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {feita ? formatarDataHora(etapa.data) : 'Aguardando…'}
                                </Typography>
                            </Box>
                        </Stack>
                    );
                })}
            </Stack>
        </Secao>
    );
}
